import { IProfile } from 'common/modules/ReactShared';
import { AnimatePresence, motion } from 'framer-motion';
import { Translate } from 'i18n';
import React, { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTypedDispatch } from 'students/stores/_utils';
import { articlesActions } from 'students/stores/articles/slice';
import { SignInButton } from 'students/views/screens/Auth';
import ArrowRightIcon from 'students/views/shared/assets/icons/arrow_right_icon.svg';
import AvatarImage from 'students/views/shared/components/AvatarImage';
import { CurrentStudentFlag } from 'students/views/shared/components/StudentFlag';
import ButtonGeneral from 'students/views/shared/components/ButtonGeneral';
import { styleInnerButton } from 'students/views/shared/components/ButtonGeneral/ButtonGeneral';
import UrlIcon from 'students/views/shared/components/UrlIcon';
import { useOutsideClick } from 'students/views/shared/hooks';
import { onLightTheme } from 'students/views/shared/styled';
import styled from 'styled-components';

import { navigationDesktopBreakPoint } from 'students/views/pages/Member/components/theme/breakpoints';
import LinkElem from 'students/views/shared/components/LinkElem';
import { useBreakPoint } from 'students/views/shared/hooks';
import { TEACHER_APP_URL } from '../GoToTeacherArea';
import { AppearanceSwitch } from './AppearanceSwitch';
import { menuActionRow } from './profileMenuStyles';

interface IProps {
  userProfile: IProfile | null;
  handleLogOut: () => void;
}

const ProfileMenu: React.FC<IProps> = ({ userProfile, handleLogOut }) => {
  const [show, setShow] = useState(false);
  const menuRef = useRef(null);
  const logoRef = useRef(null);
  const name = `${userProfile?.fname} ${userProfile?.lname}`;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const location = useLocation();
  const dispatch = useTypedDispatch();
  const isDesktop = useBreakPoint('xl');

  const toggleShow = useCallback(() => {
    setShow((prevShow) => !prevShow);
  }, [setShow]);

  const closePopover = useCallback(() => {
    setShow(false);
  }, [setShow]);

  const onLogout = useCallback(() => {
    setIsLoading(true);
    handleLogOut();
  }, [handleLogOut]);

  useOutsideClick(menuRef, closePopover, [logoRef]);

  return (
    <SWrapper>
      {userProfile ? (
        <SProfileImageButton ref={logoRef} onClick={toggleShow}>
          <SAvatarWrap>
            <SAvatar src={userProfile.avatarURL} alt={`User ${name}`} />
            <SAvatarFlag />
          </SAvatarWrap>
          <SProfileName title={name}>{name}</SProfileName>

          <SDottedIcon $isOpen={show}>
            <svg
              width="24"
              height="24"
              viewBox="0 0.5 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 5.5v.01m0 6.99v.01m0 6.99v.01M12 6.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm0 7a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm0 7a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </SDottedIcon>
        </SProfileImageButton>
      ) : (
        <SignInButton />
      )}
      <AnimatePresence>
        {show && (
          <SPopover className="profile-menu">
            <SProfileMenu
              transition={{ duration: 0.2 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              ref={menuRef}
            >
              {userProfile?.isTeacher && !isDesktop && (
                <SLink to={TEACHER_APP_URL} className="ln-flex ln-items-center">
                  <Translate str="frontend.profile.navigation.go_to_teacher_area" />
                  <UrlIcon
                    className="ln-ms-1 ln-relative ln-top-0.5"
                    url={ArrowRightIcon}
                    height="0.9rem"
                    width="0.9rem"
                    color="#0B1420"
                  />
                </SLink>
              )}
              {!isDesktop && (
                <SWhatsNewButton
                  type="button"
                  onClick={() => {
                    closePopover();
                    dispatch(articlesActions.openPanel());
                  }}
                >
                  <Translate
                    str="frontend.whats_new.title"
                    params={{ defaultValue: "What's new?" }}
                  />
                </SWhatsNewButton>
              )}

              {!isDesktop && <Devider />}

              <SLink to="/activity">
                <Translate str="frontend.profile.activity.my_activity" />
              </SLink>

              <SLink to="/my-progress">
                <Translate str="frontend.profile.navigation.my_progress" />
              </SLink>

              <SLink to={`/settings/account?from=${location.pathname}`}>
                <Translate str="frontend.profile.navigation.my_profile" />
              </SLink>

              <SLink to={`/settings?from=${location.pathname}`} onClick={closePopover}>
                <Translate str="frontend.profile.navigation.settings" />
              </SLink>

              <Devider />

              <AppearanceSwitch />

              <Devider />

              <SLogOutButton onClick={onLogout} disabled={isLoading}>
                <Translate str="frontend.profile.navigation.log_out" />
              </SLogOutButton>
            </SProfileMenu>
          </SPopover>
        )}
      </AnimatePresence>
    </SWrapper>
  );
};

export default ProfileMenu;

const Devider = styled.div`
  align-self: stretch;
  margin: 6px 8px;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const SDottedIcon = styled.div<{ $isOpen: boolean }>`
  display: none;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0.5)};
  margin-inline-start: auto;
  padding-block: 0;
  padding-inline: 2px 10px;

  svg {
    display: block;
  }

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    display: block;
    padding-inline: 0;
  }
`;

const SWrapper = styled.div`
  position: relative;
`;

const SPopover = styled.div`
  display: block;
  top: 44px;
  inset-inline-end: 0;
  position: absolute;
  z-index: 10;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    &.profile-menu {
      bottom: 19px;
      top: auto;
      inset-inline-end: -2px;
    }
  }
`;

const SProfileMenu = styled(motion.nav)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  min-width: 120px;
  padding: 10px 8px;
  overflow: hidden;
  background-color: var(--color-white);
  border: none;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  border-radius: 20px;

  a {
    ${menuActionRow}
  }

  ${onLightTheme`
    box-shadow: var(--ln-shadow-popover);

    a {
      color: var(--color-black);
    }
  `}
`;

const SLink = styled(LinkElem)`
  white-space: nowrap;
  letter-spacing: -0.02em;
`;

const SWhatsNewButton = styled.button`
  ${menuActionRow}
  white-space: nowrap;
  letter-spacing: -0.02em;
  background: none;
  border: none;
  text-align: start;

  ${onLightTheme`
    color: var(--color-black);
  `}
`;

const SLogOutButton = styled(ButtonGeneral)`
  ${menuActionRow}
  background-color: transparent;
  border: none;
  text-align: start;
  display: block;

  ${styleInnerButton()} {
    border-radius: 2px;
    letter-spacing: -0.02em;
    color: #000;
    opacity: 0.6;
    font-size: 14px;
    line-height: normal;
    padding: 0;
    justify-content: flex-start;
    transition: opacity 0.2s ease;

    ${onLightTheme`
      color: var(--color-black);
      opacity: 0.8;
    `}
  }

  &:hover:not(:disabled) > .button-inner,
  &:focus-visible > .button-inner {
    opacity: 1;
  }
`;

const SProfileImageButton = styled(ButtonGeneral)`
  position: relative;
  display: block;
  background: transparent;
  width: 100%;
  color: rgb(var(--ln-content));
  padding: 4px;
  border: none;
  transition: background 0.2s ease;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    background: rgb(var(--ln-overlay) / 0.16);
    border-radius: 16px;

    ${onLightTheme`
      background: rgb(var(--ln-overlay) / 0.1);

      &:hover {
        background: rgb(var(--ln-overlay) / 0.2);
      }
    `}

    &:hover {
      background: rgb(var(--ln-overlay) / 0.2);
    }
  }

  & > .button-inner {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: initial;
    font-weight: 600;

    @media (min-width: ${navigationDesktopBreakPoint}px) {
      gap: 8px;
    }
  }
`;

const SAvatar = styled(AvatarImage)`
  vertical-align: middle;
  height: 2rem;
  width: 2rem;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    height: 2.5rem;
    width: 2.5rem;
  }
  img {
    object-fit: contain;
  }
`;

/* The flag sits on the avatar's corner rather than beside the name: the name is hidden
   below the desktop breakpoint, and the flag is meant to be seen everywhere. */
const SAvatarWrap = styled.span`
  position: relative;
  display: inline-block;
  flex-shrink: 0;
`;

const SAvatarFlag = styled(CurrentStudentFlag)`
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: 14px;
  height: 14px;
  border: 2px solid #010615;

  ${onLightTheme`
    border-color: rgb(var(--ln-surface-nav));
  `}

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    width: 16px;
    height: 16px;
  }
`;

const SProfileName = styled.p`
  display: none;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.32px;
    text-align: start;
    overflow-wrap: anywhere;
    padding: 0;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;
